/**
 * A layout's overrides from a scene drawn at its size (multi-layout packages, ADR 0037): what differs from the primary
 * in the overridable properties (transform, size including maxWidth, anchor, visible, text fontSize, image/video fit)
 * and in keyframe values. Shared by the editor (adding and re-adapting a layout) and the API (the starter templates'
 * layouts from their per-shape variants, a template instantiated in another shape). Pure and zod-free.
 */
import { layoutId, timeKey, timelinePhases, trackKey } from './layouts.ts';
import type { Element, Layout, LayoutOverride, Scene } from './scene.ts';
import { walkElements } from './scene.ts';

export const OVERRIDE_TRANSFORM_KEYS = ['x', 'y', 'rotation', 'scaleX', 'scaleY', 'originX', 'originY', 'opacity'] as const;
export const OVERRIDE_SIZE_KEYS = ['width', 'height', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight'] as const;
/** What an element without an anchor is anchored by. */
export const DEFAULT_ANCHOR = { horizontal: 'left', vertical: 'top', to: 'parent' } as const;

const same = (a: unknown, b: unknown): boolean => a === b || JSON.stringify(a) === JSON.stringify(b);

/** What a layout's element `el` overrides of the primary's element `p` (undefined: nothing). */
export function overrideOf(p: Element, el: Element): LayoutOverride | undefined {
  const o: LayoutOverride = {};
  const transform: NonNullable<LayoutOverride['transform']> = {};
  for (const k of OVERRIDE_TRANSFORM_KEYS) if (el.transform[k] !== p.transform[k]) transform[k] = el.transform[k];
  if (Object.keys(transform).length) o.transform = transform;
  const size: NonNullable<LayoutOverride['size']> = {};
  for (const k of OVERRIDE_SIZE_KEYS) {
    const v = el.size[k];
    if (v !== undefined && v !== p.size[k]) (size as Record<string, unknown>)[k] = v;
  }
  if (Object.keys(size).length) o.size = size;
  if (!same(p.anchor ?? DEFAULT_ANCHOR, el.anchor ?? DEFAULT_ANCHOR) || (el.anchor !== undefined && p.anchor === undefined && !same(el.anchor, DEFAULT_ANCHOR))) o.anchor = { ...(el.anchor ?? DEFAULT_ANCHOR) };
  if (el.visible !== p.visible) o.visible = el.visible;
  if (el.type === 'text' && p.type === 'text' && el.style.fontSize !== p.style.fontSize) o.fontSize = el.style.fontSize;
  if ((el.type === 'image' || el.type === 'video') && (p.type === 'image' || p.type === 'video') && el.fit !== p.fit) o.fit = el.fit;
  return Object.keys(o).length ? o : undefined;
}

/**
 * The layout that turns `primary` (a single-layout scene) into `flat`, a scene of the same graphic at another size:
 * the same elements (by id) and the same timeline (phases, tracks and keyframe times). Throws where `flat` differs in
 * anything a layout cannot express (another structure or keyframe timing); other differences, e.g. a padding, are
 * not detected here: `sceneForLayout` of the result shows the primary's.
 */
export function layoutFrom(primary: Scene, flat: Scene, label?: string): Layout {
  const { width, height } = flat.canvas;
  const elements = new Map<string, Element>();
  walkElements(primary.elements, (el) => elements.set(el.id, el));
  const overrides: NonNullable<Layout['overrides']> = {};
  walkElements(flat.elements, (el) => {
    const p = elements.get(el.id);
    if (!p || p.type !== el.type) throw new Error(`layoutFrom: element "${el.id}" is not in the primary as a ${el.type}`);
    const o = overrideOf(p, el);
    if (o) overrides[el.id] = o;
  });
  const keyframes: NonNullable<Layout['keyframes']> = {};
  const phases = new Map(timelinePhases(primary.timeline));
  for (const [path, phase] of timelinePhases(flat.timeline)) {
    const pp = phases.get(path);
    if (!pp || pp.tracks.length !== phase.tracks.length) throw new Error(`layoutFrom: the tracks of ${path} differ`);
    phase.tracks.forEach((tr, i) => {
      const pt = pp.tracks[i]!;
      if (pt.element !== tr.element || pt.property !== tr.property || pt.keyframes.length !== tr.keyframes.length) throw new Error(`layoutFrom: track ${i} of ${path} differs`);
      tr.keyframes.forEach((kf, k) => {
        const pk = pt.keyframes[k]!;
        if (pk.time !== kf.time) throw new Error(`layoutFrom: keyframe times of ${trackKey(path, tr)} differ`);
        if (!same(pk.value, kf.value)) (keyframes[trackKey(path, tr)] ??= {})[timeKey(kf.time)] = typeof kf.value === 'object' ? { ...kf.value } : kf.value;
      });
    });
  }
  return {
    id: layoutId(width, height),
    ...(label ? { label } : {}),
    width,
    height,
    ...(Object.keys(overrides).length ? { overrides } : {}),
    ...(Object.keys(keyframes).length ? { keyframes } : {}),
  };
}
